<template>
  <CChartLine v-if="tank"
    :datasets="datasets"
    :options="options"
    :labels="labels"
  />
</template>

<script>
import { CChartLine } from '@coreui/vue-chartjs'
import { getStyle, hexToRgba } from '@coreui/utils/src'
import {unitSign, ltrToUnit, tempC2F, sleep} from "@/common/utils";

// 리터 → 탱크 표시단위(l/g/k/d). kL·드럼은 값이 작아 소수 1자리를 남긴다
function transVol(to,val){
  const v = ltrToUnit(val, to);
  return (to==='k' || to==='d') ? Number(v.toFixed(1)) : Math.round(v);
}

// 좌측 용량 축 눈금 포맷: 1000 이상은 k 단위로 축약 (예: 12500 -> 12.5k)
function volTickLabel(val){
  const num = Number(val);
  if (!isFinite(num)) return val;
  if (Math.abs(num) < 1000) return `${num}`;
  const k = num / 1000;
  // 소수점 1자리까지만 표기하되 .0 은 떼어낸다
  return `${parseFloat(k.toFixed(1))}k`;
}


export default {
  name: 'TankLineChart',
  components: {
    CChartLine
  },
  props:{
    tank: { type: Object, default: null },
    oilColor: { type: String, default: null },
  },
  data () {
    return {
      datasets: [],
      options: {},
      labels: [],
    }
  },
  computed: {
  },

  created() {
    // console.log('[TankLineChart]-------Created--',this.tank.name);
    this.setOptions();
  },

  methods: {
    async setChartData(data){
      // console.log('[TankLineChart]------ count=', data?.ovms?.length );


      const tank = this.tank;
      const {unitOfTmp} = this.tank;
      const {unitOfVol} = this.tank;

      this.setOptions();
      await sleep(200);

      if(!data?.ovms.length) {
        this.datasets = [];
        return
      }

      let ovms = [], ovcs = [], tmps = [];  // gross vol, net vol, 온도

      ovms = data.ovms.map(val=> transVol(unitOfVol, val));
      ovcs = data.ovcs.map(val=> transVol(unitOfVol, val));

      if( unitOfTmp==='f'){
        tmps = data.tmps.map(val=> {return tempC2F(val)} );
        tmps = data.tmps.map(val=> {return Math.round(val * (9 / 5) + 32)} );
      }else{
        tmps = data.tmps;
      }

      this.datasets = [
        {
          label: `Gross (${unitSign(unitOfVol) || 'L'})`,
          backgroundColor: hexToRgba( tank.color, 20),
          borderColor: this.oilColor,
          pointHoverBackgroundColor: this.oilColor,
          borderWidth: 3,
          data: ovms,
          yAxisID: 'volume'
        },
        {
          label: `Net (${unitSign(unitOfVol) || 'L'})`,
          backgroundColor: 'transparent',
          borderColor: getStyle('success') || '#4dbd74',
          pointHoverBackgroundColor: tank.color,
          borderWidth: 1,
          data: ovcs,
          yAxisID: 'volume'
        },

        {
          label: '온도',
          backgroundColor: 'transparent',
          borderColor: getStyle('warning') || '#f9b115',
          pointHoverBackgroundColor: getStyle('warning') || '#f9b115',
          borderWidth: 1,
          borderDash: [8, 5],
          data: tmps,
          yAxisID: 'temp'
        }
      ];

      // 안전재고량 라인 (붉은색 점선). 설정된 탱크만 표시
      const safetyVol = Number(tank.safetyVolume) || 0;
      if( safetyVol > 0 ){
        this.datasets.push({
          label: `안전재고량 (${unitSign(unitOfVol) || 'L'})`,
          backgroundColor: 'transparent',
          borderColor: getStyle('danger') || '#f86c6b',
          pointHoverBackgroundColor: getStyle('danger') || '#f86c6b',
          borderWidth: 2,
          borderDash: [6, 4],
          pointRadius: 0,
          data: new Array(ovms.length).fill( transVol(unitOfVol, safetyVol) ),
          yAxisID: 'volume'
        });
      }

      this.labels = data.labels;

    },

    setOptions(){
      const tank = this.tank;

      // console.log('----------TankLineChart------- tankVolume--->', transVol('g',tank.tankVolume));
      // console.log( "---------TankLineChart------- tankData Object --->", data );
      // console.log( "---------TankLineChart------- oilColor --->", this.oilColor );


      const {unitOfTmp} = this.tank;
      const {unitOfVol} = this.tank;


      this.options = {
        title: {
          display: false,
          fontStyle: "bold",
          fullWidth: false,
          padding: 1,
          position: "top",
          text: `${tank.tankCode} ${tank.name}`,
          weight: 1000
        },
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: "top",
        },
        scales: {
          xAxes: [{
            gridLines: {
              drawOnChartArea: false
            }
          }],
          yAxes: [
            {
              id: 'volume',
              ticks: {
                beginAtZero: true,
                maxTicksLimit: 10,
                // 눈금 간격은 chart.js 가 단위에 맞춰 고르게 한다 (종전 stepSize 식은 연산 순서 오류로 200/1000 만 나왔다)
                max: transVol(unitOfVol, tank.tankVolume),
                callback: volTickLabel,
              },
              gridLines: {
                display: true
              },

            },
            {
              id: 'temp',
              // 고정 눈금(min/max) 제거: 데이터 범위에 따라 상대적 스케일로 자동 조정
              ticks: {
                beginAtZero: false,
                maxTicksLimit: 10
              },
              position: 'right',
              gridLines: {
                display: true
              },
              scaleLabel: {
                labelString: `온도 (${unitSign(unitOfTmp)})`,
                display: true
              }

            }
          ]
        },
        elements: {
          point: {
            radius: 0,
            hitRadius: 10,
            hoverRadius: 4,
            hoverBorderWidth: 3
          }
        }
      };
    }

  }
}
</script>
